import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import type { Action, UnknownAction, Dispatch, Middleware } from "redux";
import Session from "users/session/session";

// A thunk-like function that also receives SignalR invoke
export type SignalThunk<
  S = unknown,
  A extends Action = UnknownAction,
  R = void | Promise<void>
> = (dispatch: Dispatch<A>, getState: () => S, invoke: HubConnection["invoke"]) => R;

// Callback for a server event that returns a SignalThunk
export type EventCallback<S = unknown, A extends Action = UnknownAction> = (
  ...args: unknown[]
) => SignalThunk<S, A>;

export interface WithCallbacks<S = unknown, A extends Action = UnknownAction> {
  add(name: string, callback: EventCallback<S, A>): WithCallbacks<S, A>;
  callbackMap: Map<string, EventCallback<S, A>>;
}

export const withCallbacks = <S = unknown, A extends Action = UnknownAction>(): WithCallbacks<S, A> => {
  const callbackMap = new Map<string, EventCallback<S, A>>();
  return {
    add(name: string, callback: EventCallback<S, A>) {
      callbackMap.set(name, callback);
      return this;
    },
    callbackMap,
  };
};

export interface MiddlewareConfig<S = unknown, A extends Action = UnknownAction> {
  callbacks: WithCallbacks<S, A>;
  connection: HubConnection;
  onStart?: () => void;
  shouldConnectionStartImmediately?: boolean;
}

const isFunction = (value: unknown): value is (...args: unknown[]) => unknown => typeof value === "function";

const connect = async (connection: HubConnection): Promise<void> => {
  const token = Session.loadFromStorage(() => { }).authToken?.value;
  if (!token) return; // don't connect unauthenticated
  if (connection.state !== HubConnectionState.Disconnected) return; // already connected/connecting

  try {
    await connection.start();
    console.log("SignalR connected");
  } catch (err) {
    console.error("SignalR connection error:", err);
  }
};

const reduxSignalRMiddleware =
  <S = unknown, A extends Action = UnknownAction>({
    callbacks,
    onStart,
    connection,
    shouldConnectionStartImmediately = true,
  }: MiddlewareConfig<S, A>): Middleware<object, S> =>
    (store) => {
      // Register handlers idempotently (remove previous handlers for each name first)
      for (const [name, callback] of callbacks.callbackMap) {
        connection.off(name);
        connection.on(name, (...args: unknown[]) => {
          try {
            const thunk = callback(...args);
            if (isFunction(thunk)) {
              void Promise.resolve(
                thunk(
                  store.dispatch as Dispatch<A>,
                  store.getState as () => S,
                  connection.invoke.bind(connection)
                )
              ).catch((err) => {
                console.error(`SignalR handler for "${name}" failed:`, err);
              });
            }
          } catch (err) {
            console.error(`SignalR callback for "${name}" threw:`, err);
          }
        });
      }

      if (shouldConnectionStartImmediately) {
        void connect(connection).then(() => onStart?.());
      }

      // Reconnect after auth lifecycle
      document.addEventListener("loggedIn", () => {
        void connect(connection).then(() => onStart?.());
      });

      return (next) => (action) => {
        if (isFunction(action)) {
          // Treat function actions as SignalThunks
          try {
            return (action as unknown as SignalThunk<S, A>)(
              store.dispatch as Dispatch<A>,
              store.getState as () => S,
              connection.invoke.bind(connection)
            );
          } catch (err) {
            console.error("SignalR thunk execution failed:", err);
            return undefined as unknown as A;
          }
        }
        return next(action as A);
      };
    };

export default reduxSignalRMiddleware;
